import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Search, Trash2 } from "lucide-react";
import DetailDrawer from "../DetailDrawer";
import {
  getAdminSaleById,
  updateAdminSaleById,
} from "../../../services/api";
import "../SessionsView/SessionsView.css";

const buildEmptyItem = () => ({
  id: Date.now() + Math.random(),
  quantity: 1,
  price: "",
  serialNumber: "",
  selectedVariant: "",
  variantSearch: "",
  showVariantDropdown: false,
  variantMeta: null,
});

const toDateTimeLocalValue = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  const offsetMs = parsed.getTimezoneOffset() * 60 * 1000;
  return new Date(parsed.getTime() - offsetMs).toISOString().slice(0, 16);
};

const splitDateTimeValue = (value) => {
  const localValue = toDateTimeLocalValue(value);
  if (!localValue) {
    return { sale_date_date: "", sale_date_time: "" };
  }

  const [sale_date_date, sale_date_time] = localValue.split("T");
  return {
    sale_date_date: sale_date_date || "",
    sale_date_time: sale_date_time || "",
  };
};

const parsePriceInput = (value) => {
  const normalized = String(value ?? "")
    .replace(/,/g, ".")
    .replace(/[^\d.]/g, "")
    .trim();

  if (!normalized) return 0;
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getVariantDisplayName = (variant) => {
  if (!variant) return "";
  const modelCode = variant.product_model_code?.trim();
  const fallbackModelCode = variant.productModel?.product_model_code?.trim();
  const description = variant.product_model_description?.trim();
  return modelCode || fallbackModelCode || description || "";
};

const isBrokenModelCode = (variant) => {
  const modelCode = String(
    variant?.product_model_code || variant?.productModel?.product_model_code || "",
  ).trim();
  return !modelCode || modelCode === "000000";
};

const getVariantSearchLabel = (variant) => getVariantDisplayName(variant);

const getColourPresentation = (colour) => {
  const normalizedColour = String(colour || "").trim().toLowerCase().replace(/\s+/g, " ");
  if (!normalizedColour) return null;

  const tokenMap = {
    black: "#111827",
    white: "#ffffff",
    blue: "#2563eb",
    red: "#dc2626",
    green: "#16a34a",
    yellow: "#eab308",
    orange: "#f97316",
    purple: "#9333ea",
    pink: "#ec4899",
    brown: "#8b5e3c",
    bronze: "#b87333",
    cream: "#f3e7cf",
    silver: "#9ca3af",
    gold: "#d4a017",
    grey: "#6b7280",
    gray: "#6b7280",
  };

  const tokens = normalizedColour.split(" ").filter(Boolean);
  const matchedColours = tokens.map((token) => tokenMap[token]).filter(Boolean);
  if (matchedColours.length === 0) {
    return {
      badgeStyle: {
        background: "#f8fafc",
        borderColor: "#d1d5db",
        color: "#6b7280",
      },
    };
  }

  const primaryColour = matchedColours[0];
  const background =
    matchedColours.length === 1
      ? primaryColour
      : `linear-gradient(135deg, ${matchedColours.join(", ")})`;

  return {
    badgeStyle: {
      background: "#f8fafc",
      borderColor: `${primaryColour}55`,
      color: primaryColour === "#ffffff" ? "#4b5563" : primaryColour,
    },
    swatchStyle: {
      background,
      borderColor: primaryColour === "#ffffff" ? "#d1d5db" : primaryColour,
    },
  };
};

const ColourMeta = ({ label, colour }) => {
  if (!colour) return null;
  const colourPresentation = getColourPresentation(colour);

  return (
    <span className="sale-colour-meta">
      {label ? <span className="sale-colour-meta-label">{label}</span> : null}
      <span className="sale-colour-pill" style={colourPresentation?.badgeStyle}>
        {colourPresentation?.swatchStyle ? (
          <span
            className="sale-colour-swatch"
            style={colourPresentation.swatchStyle}
            aria-hidden="true"
          />
        ) : null}
        <span>{colour}</span>
      </span>
    </span>
  );
};

const normalizeItem = (item = {}) => ({
  ...buildEmptyItem(),
  selectedVariant: item.product_id || item.product?.product_id || "",
  variantSearch:
    item.product?.product_model_code ||
    item.product?.product_model_description ||
    item.product_name ||
    "",
  variantMeta: {
    categoryName: item.product?.productGroup?.product_group_description || "",
    productName: item.product?.product_description || item.product_name || "",
    modelName:
      item.product?.product_model_code ||
      item.product?.product_model_description ||
      item.product_name ||
      "",
    modelDescription: item.product?.product_model_description || "",
    colour: item.product?.colour || "",
    displayName:
      item.product?.product_model_code ||
      item.product?.product_model_description ||
      item.product_name ||
      "",
    productId: item.product_id || item.product?.product_id || null,
  },
  quantity: Math.abs(Number.parseInt(item.quantity, 10) || 1),
  price: Number.parseFloat(item.unit_price || 0).toFixed(2),
  serialNumber: item.serial_number || "",
});

export default function SaleCorrectionDrawer({
  isOpen,
  saleId,
  onClose,
  onSaved,
  t,
  showSuccess,
  showError,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sale, setSale] = useState(null);
  const [allVariants, setAllVariants] = useState({});
  const [loadingVariantsByItem, setLoadingVariantsByItem] = useState({});
  const searchTimeoutsRef = useRef({});
  const latestSearchRef = useRef({});
  const [form, setForm] = useState({
    sale_date_date: "",
    sale_date_time: "",
    check_in_id: "",
    notes: "",
    clear_receipt_image: false,
    receiptFile: null,
    items: [],
  });

  const isReturn = useMemo(() => {
    if (!sale) return false;
    return Number(sale.total_amount) < 0;
  }, [sale]);

  useEffect(() => {
    if (!isOpen || !saleId) {
      setSale(null);
      setAllVariants({});
      setLoadingVariantsByItem({});
      setForm({
        sale_date_date: "",
        sale_date_time: "",
        check_in_id: "",
        notes: "",
        clear_receipt_image: false,
        receiptFile: null,
        items: [],
      });
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setSale(null);
        const saleResponse = await getAdminSaleById(saleId);
        if (cancelled) return;
        const loadedSale = saleResponse?.data || null;
        const splitDateTime = splitDateTimeValue(loadedSale?.sale_date);

        setSale(loadedSale);
        setForm({
          ...splitDateTime,
          check_in_id: String(loadedSale?.check_in_id || ""),
          notes: loadedSale?.notes || "",
          clear_receipt_image: false,
          receiptFile: null,
          items: Array.isArray(loadedSale?.items)
            ? loadedSale.items.map(normalizeItem)
            : [buildEmptyItem()],
        });
      } catch (error) {
        if (!cancelled) {
          showError(
            error?.response?.data?.error ||
              t("admin.salesReports.corrections.failedToLoad", {
                defaultValue: "Failed to load sale details.",
              }),
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [isOpen, saleId, showError, t]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".searchable-dropdown")) {
        setForm((previous) => ({
          ...previous,
          items: previous.items.map((item) => ({
            ...item,
            showVariantDropdown: false,
          })),
        }));
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const currentTimeouts = searchTimeoutsRef.current;
    return () => {
      Object.values(currentTimeouts).forEach((timeoutId) => clearTimeout(timeoutId));
    };
  }, []);

  const fetchVariantOptions = async (itemId, query = "") => {
    const normalizedQuery = String(query || "").trim();
    latestSearchRef.current[itemId] = normalizedQuery;

    setLoadingVariantsByItem((previous) => ({ ...previous, [itemId]: true }));

    try {
      const response = await axios.get("/api/product-hierarchy/variants", {
        params: {
          q: normalizedQuery || undefined,
          limit: 30,
        },
      });

      if (
        response.data.success &&
        latestSearchRef.current[itemId] === normalizedQuery
      ) {
        setAllVariants((previous) => ({
          ...previous,
          [itemId]: (response.data.data || []).filter(
            (variant) => !isBrokenModelCode(variant),
          ),
        }));
      }
    } catch (_) {
      if (latestSearchRef.current[itemId] === normalizedQuery) {
        setAllVariants((previous) => ({
          ...previous,
          [itemId]: [],
        }));
      }
    } finally {
      if (latestSearchRef.current[itemId] === normalizedQuery) {
        setLoadingVariantsByItem((previous) => ({ ...previous, [itemId]: false }));
      }
    }
  };

  const queueVariantFetch = (itemId, query = "") => {
    if (searchTimeoutsRef.current[itemId]) {
      clearTimeout(searchTimeoutsRef.current[itemId]);
    }

    searchTimeoutsRef.current[itemId] = setTimeout(() => {
      fetchVariantOptions(itemId, query);
    }, 250);
  };

  const handleVariantSelect = (index, variant) => {
    setForm((previous) => {
      const nextItems = [...previous.items];
      nextItems[index] = {
        ...nextItems[index],
        selectedVariant: variant.product_id,
        variantSearch: getVariantSearchLabel(variant),
        showVariantDropdown: false,
        variantMeta: {
          categoryName: variant.productGroup?.product_group_description || "",
          productName: variant.product_description || "",
          modelName: getVariantDisplayName(variant),
          modelDescription: variant.product_model_description || "",
          colour: variant.colour || "",
          displayName: getVariantSearchLabel(variant),
          productId: variant.product_id,
        },
      };

      return {
        ...previous,
        items: nextItems,
      };
    });
  };

  const handleItemChange = (index, field, value) => {
    setForm((previous) => {
      const nextItems = [...previous.items];
      const nextItem = { ...nextItems[index], [field]: value };

      if (field === "variantSearch") {
        nextItem.showVariantDropdown = true;
        nextItem.selectedVariant = "";
        nextItem.variantMeta = null;
      }

      nextItems[index] = nextItem;
      return {
        ...previous,
        items: nextItems,
      };
    });
  };

  const addItem = () => {
    setForm((previous) => ({
      ...previous,
      items: [...previous.items, buildEmptyItem()],
    }));
  };

  const removeItem = (index) => {
    setForm((previous) => ({
      ...previous,
      items:
        previous.items.length > 1
          ? previous.items.filter((_, itemIndex) => itemIndex !== index)
          : previous.items,
    }));
  };

  const handleSave = async () => {
    if (!form.sale_date_date || !form.sale_date_time) {
      showError("Sale date and time are required.");
      return;
    }

    const invalidItemIndex = form.items.findIndex(
      (item) => !String(item.variantSearch || "").trim(),
    );
    if (invalidItemIndex !== -1) {
      showError(`Item ${invalidItemIndex + 1}: model is required.`);
      return;
    }

    const invalidQuantityIndex = form.items.findIndex((item) => {
      const parsedQuantity = Number.parseInt(item.quantity, 10);
      return !Number.isInteger(parsedQuantity) || parsedQuantity <= 0;
    });
    if (invalidQuantityIndex !== -1) {
      showError(`Item ${invalidQuantityIndex + 1}: quantity is required.`);
      return;
    }

    const invalidPriceIndex = form.items.findIndex(
      (item) => String(item.price ?? "").trim() === "",
    );
    if (invalidPriceIndex !== -1) {
      showError(`Item ${invalidPriceIndex + 1}: unit price is required.`);
      return;
    }

    const composedDateTime =
      form.sale_date_date && form.sale_date_time
        ? `${form.sale_date_date}T${form.sale_date_time}`
        : "";

    try {
      setSaving(true);
      await updateAdminSaleById(saleId, {
        sale_date: composedDateTime ? new Date(composedDateTime).toISOString() : "",
        check_in_id: form.check_in_id,
        notes: form.notes,
        items: form.items.map((item) => ({
          product_id: item.selectedVariant
            ? Number.parseInt(item.selectedVariant, 10)
            : null,
          product_name:
            item.variantMeta?.productName || item.variantSearch.trim() || "",
          category_name: item.variantMeta?.categoryName || "",
          model_name: item.variantMeta?.modelName || item.variantSearch || "",
          variant_name: item.variantMeta?.displayName || item.variantSearch || "",
          quantity: Number.parseInt(item.quantity, 10) || 1,
          unit_price: parsePriceInput(item.price),
          serial_number: item.serialNumber || null,
        })),
        clear_receipt_image: form.clear_receipt_image,
        receiptFile: form.receiptFile,
      });

      showSuccess(
        t("admin.salesReports.corrections.saveSuccess", {
          defaultValue: "Sale updated successfully.",
        }),
      );
      if (typeof onSaved === "function") onSaved();
      onClose();
    } catch (error) {
      showError(
        error?.response?.data?.error ||
          t("admin.salesReports.corrections.saveFailed", {
            defaultValue: "Failed to update sale.",
          }),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <DetailDrawer
      isOpen={isOpen}
      onClose={saving ? () => {} : onClose}
      title={t("admin.salesReports.corrections.drawerTitle", {
        defaultValue: "Sale Correction",
      })}
      subtitle={sale?.user?.full_name || ""}
    >
      <div className="admin-correction-drawer">
        {loading ? (
          <div className="admin-correction-skeleton">
            <div className="skeleton-box admin-correction-skeleton-line" />
            <div className="skeleton-box admin-correction-skeleton-line" />
            <div className="skeleton-box admin-correction-skeleton-card" />
          </div>
        ) : (
          <>
              <section className="admin-correction-section">
                <div className="admin-correction-grid">
                  <div className="admin-correction-summary">
                    <span>Promoter</span>
                    <strong>{sale?.user?.full_name || "-"}</strong>
                  </div>
                  <div className="admin-correction-summary">
                    <span>Outlet</span>
                    <strong>
                      {sale?.outlet?.bp_outlet_name || sale?.checkIn?.outlet_name || "-"}
                    </strong>
                  </div>
                  <div className="admin-correction-summary">
                    <span>Current Session</span>
                    <strong>#{sale?.check_in_id || "-"}</strong>
                  </div>
                  <div className="admin-correction-summary">
                    <span>Type</span>
                    <strong>{isReturn ? "Return" : "Sale"}</strong>
                  </div>
                </div>
              </section>

              <section className="admin-correction-section">
                <h5>Sale Details</h5>
                <div className="admin-correction-form">
                  <label>
                    Sale Date
                    <span className="required-asterisk" aria-hidden="true">*</span>
                  </label>
                  <div className="admin-correction-date-time-grid">
                    <div className="admin-correction-date-time-field">
                      <span className="admin-correction-date-time-title">
                        Date
                        <span className="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <input
                        type="date"
                        className="admin-sessions-input"
                        value={form.sale_date_date}
                        onChange={(event) =>
                          setForm((previous) => ({
                            ...previous,
                            sale_date_date: event.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="admin-correction-date-time-field">
                      <span className="admin-correction-date-time-title">
                        Time
                        <span className="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <input
                        type="time"
                        className="admin-sessions-input"
                        value={form.sale_date_time}
                        onChange={(event) =>
                          setForm((previous) => ({
                            ...previous,
                            sale_date_time: event.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>

                  <label>Notes</label>
                  <textarea
                    className="admin-correction-textarea"
                    rows={3}
                    value={form.notes}
                    onChange={(event) =>
                      setForm((previous) => ({
                        ...previous,
                        notes: event.target.value,
                      }))
                    }
                  />

                  <label>Receipt</label>
                  <input
                    type="file"
                    className="admin-correction-file-input"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={(event) =>
                      setForm((previous) => ({
                        ...previous,
                        receiptFile: event.target.files?.[0] || null,
                      }))
                    }
                  />
                  <div className="admin-correction-receipt-meta">
                    {form.receiptFile ? (
                      <span>Selected: {form.receiptFile.name}</span>
                    ) : sale?.receipt_image_url ? (
                      <span>Existing receipt will be kept unless removed.</span>
                    ) : (
                      <span>No receipt uploaded.</span>
                    )}
                  </div>
                  <label className="admin-correction-checkbox">
                    <input
                      type="checkbox"
                      checked={form.clear_receipt_image}
                      onChange={(event) =>
                        setForm((previous) => ({
                          ...previous,
                          clear_receipt_image: event.target.checked,
                        }))
                      }
                    />
                    <span>Remove existing receipt</span>
                  </label>
                </div>
              </section>

              <section className="admin-correction-section">
                <div className="admin-correction-section-header">
                  <h5>Items</h5>
                  <button type="button" className="admin-btn-secondary" onClick={addItem}>
                    Add Item
                  </button>
                </div>
                <div className="admin-correction-items">
                  {form.items.map((item, index) => {
                    const filteredVariants = (allVariants[item.id] || []).filter(
                      (variant) => !isBrokenModelCode(variant),
                    );
                    const isLoadingVariants = Boolean(loadingVariantsByItem[item.id]);

                    return (
                      <div key={item.id} className="sale-item-section sale-item-grid-section admin-correction-item-sale">
                        <div className="sale-item-section-header">
                          <h4>Item {index + 1}</h4>
                          {form.items.length > 1 ? (
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              className="sale-item-remove-btn"
                            >
                              <Trash2 size={14} aria-hidden="true" />
                              Remove
                            </button>
                          ) : null}
                        </div>

                        <div className="form-group sale-entry-grid-model">
                          <label className="sale-item-label sale-item-label-model">
                            Model / Variant
                            <span className="required-asterisk" aria-hidden="true">*</span>
                          </label>
                          <div className="searchable-dropdown">
                            <div className="searchable-dropdown-input">
                              <Search size={18} className="searchable-dropdown-icon" />
                              <input
                                type="text"
                                value={item.variantSearch}
                                onChange={(event) => {
                                  handleItemChange(index, "variantSearch", event.target.value);
                                  queueVariantFetch(item.id, event.target.value);
                                }}
                                onFocus={() => {
                                  handleItemChange(index, "showVariantDropdown", true);
                                  queueVariantFetch(item.id, item.variantSearch);
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter" && filteredVariants.length > 0) {
                                    event.preventDefault();
                                    handleVariantSelect(index, filteredVariants[0]);
                                  } else if (event.key === "Escape") {
                                    handleItemChange(index, "showVariantDropdown", false);
                                  }
                                }}
                                placeholder={isLoadingVariants ? "Loading models..." : "Search model"}
                                className="searchable-dropdown-text-input"
                              />
                            </div>
                            {item.showVariantDropdown ? (
                              <div className="dropdown-list">
                                {isLoadingVariants ? (
                                  <div className="dropdown-item disabled">Loading models...</div>
                                ) : filteredVariants.length > 0 ? (
                                  filteredVariants.slice(0, 30).map((variant) => (
                                    <div
                                      key={variant.product_id}
                                      className="dropdown-item"
                                      onClick={() => handleVariantSelect(index, variant)}
                                    >
                                      <div className="sale-model-option-name">
                                        {getVariantSearchLabel(variant)}
                                      </div>
                                      <div className="sale-model-option-meta">
                                        <ColourMeta label="Colour" colour={variant.colour} />
                                        {variant.colour && variant.product_model_description ? " · " : ""}
                                        {variant.product_model_description || ""}
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <div className="dropdown-item disabled">
                                    No models found. You can still type the model manually.
                                  </div>
                                )}
                              </div>
                            ) : null}
                          </div>

                          {item.variantMeta ? (
                            <div className="sale-model-meta">
                              <div>
                                {item.variantMeta.colour ? (
                                  <ColourMeta label="Colour" colour={item.variantMeta.colour} />
                                ) : (
                                  <>
                                    <strong>Colour</strong> Not available
                                  </>
                                )}
                              </div>
                              <div>
                                <strong>Model description</strong>{" "}
                                {item.variantMeta.modelDescription || "Not available"}
                              </div>
                            </div>
                          ) : null}
                        </div>

                        <div className="sale-entry-inline-row">
                          <div className="form-group sale-entry-grid-qty">
                            <label>
                              Quantity
                              <span className="required-asterisk" aria-hidden="true">*</span>
                            </label>
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(event) =>
                                handleItemChange(index, "quantity", event.target.value)
                              }
                              placeholder="1"
                            />
                          </div>

                          <div className="form-group sale-entry-grid-price">
                            <label>
                              Unit Price
                              <span className="required-asterisk" aria-hidden="true">*</span>
                            </label>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={item.price}
                              onChange={(event) =>
                                handleItemChange(index, "price", event.target.value)
                              }
                              placeholder="0.00"
                            />
                          </div>
                        </div>

                        <div className="form-group">
                          <label>Serial Number</label>
                          <input
                            type="text"
                            className="admin-sessions-input"
                            value={item.serialNumber}
                            onChange={(event) =>
                              handleItemChange(index, "serialNumber", event.target.value)
                            }
                            placeholder="Serial number"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              <div className="admin-correction-actions admin-correction-actions-single">
                <button
                  type="button"
                  className="admin-btn-primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
          </>
        )}

        {saving ? (
          <div className="admin-correction-overlay" aria-busy="true">
            <div className="admin-reports-export-overlay-spinner"></div>
          </div>
        ) : null}
      </div>
    </DetailDrawer>
  );
}
